/**
 * Notification preferences — Zustand local cache.
 *
 * The server-of-record table `notification_preferences` (migration
 * 20260429000004) is the source of truth. This store is a thin local
 * cache so the toggle UI renders instantly on a cold start before the
 * `useNotificationPrefs` hook has resolved its first network read.
 *
 * Every mutation flows through `useNotificationPrefs` (which calls
 * `notificationPrefsService.updatePrefs`); the server response then
 * hydrates this cache via `hydrateFromServer`.
 *
 * On logout the consumer should call `reset()` to clear another user's
 * preferences from local storage.
 */

import { create } from 'zustand'
import { MMKV } from 'react-native-mmkv'
import {
  DEFAULT_NOTIFICATION_PREFS,
  type NotificationPrefs,
} from '../services/notification-prefs.service'

let storage: MMKV
try {
  storage = new MMKV({ id: 'tago-notification-prefs' })
} catch {
  // SSR: MMKV constructor fails during static rendering
  storage = { getString: () => undefined, set: () => {}, delete: () => {} } as unknown as MMKV
}

const STORAGE_KEY = 'notification_prefs_cache'

type PrefsValues = Omit<NotificationPrefs, 'updatedAt'>

interface NotificationPrefsState extends PrefsValues {
  /**
   * `true` once the cache has been seeded — either from MMKV on cold
   * start, from the server on first hydration, or from an optimistic
   * patch. Used by `useNotificationPrefs` to choose between cache and
   * defaults on the first render.
   */
  hasHydrated: boolean

  /**
   * Replace the cache entirely with a server response.
   */
  hydrateFromServer: (next: PrefsValues) => void

  /**
   * Apply a partial patch (used for optimistic flips before the server
   * round-trip lands).
   */
  applyPatch: (patch: Partial<PrefsValues>) => void

  /**
   * Snapshot the current values without `hasHydrated`.
   */
  snapshot: () => PrefsValues

  /**
   * Wipe the cache. Call on logout so the next signed-in user doesn't
   * inherit previous prefs.
   */
  reset: () => void
}

function persist(values: PrefsValues) {
  try {
    storage.set(STORAGE_KEY, JSON.stringify(values))
  } catch {
    // SSR or storage unavailable
  }
}

function loadInitial(): { values: PrefsValues; hasHydrated: boolean } {
  try {
    const raw = storage.getString(STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<PrefsValues>
      return {
        values: { ...DEFAULT_NOTIFICATION_PREFS, ...parsed },
        hasHydrated: true,
      }
    }
  } catch {
    // Corrupt JSON — fall through to defaults
  }
  return { values: { ...DEFAULT_NOTIFICATION_PREFS }, hasHydrated: false }
}

const initial = loadInitial()

export const useNotificationPrefsStore = create<NotificationPrefsState>((set, get) => ({
  ...initial.values,
  hasHydrated: initial.hasHydrated,

  hydrateFromServer: (next) => {
    persist(next)
    set({ ...next, hasHydrated: true })
  },

  applyPatch: (patch) => {
    set((state) => {
      const next: PrefsValues = {
        pushEnabled: patch.pushEnabled ?? state.pushEnabled,
        pushBookingUpdates: patch.pushBookingUpdates ?? state.pushBookingUpdates,
        pushTripReminders: patch.pushTripReminders ?? state.pushTripReminders,
        pushNewMessages: patch.pushNewMessages ?? state.pushNewMessages,
        pushHostBookings: patch.pushHostBookings ?? state.pushHostBookings,
        pushHostCancellations: patch.pushHostCancellations ?? state.pushHostCancellations,
        pushHostPayouts: patch.pushHostPayouts ?? state.pushHostPayouts,
        pushPromotions: patch.pushPromotions ?? state.pushPromotions,
        emailEnabled: patch.emailEnabled ?? state.emailEnabled,
        smsEnabled: patch.smsEnabled ?? state.smsEnabled,
      }
      persist(next)
      return { ...next, hasHydrated: true }
    })
  },

  snapshot: () => {
    const s = get()
    return {
      pushEnabled: s.pushEnabled,
      pushBookingUpdates: s.pushBookingUpdates,
      pushTripReminders: s.pushTripReminders,
      pushNewMessages: s.pushNewMessages,
      pushHostBookings: s.pushHostBookings,
      pushHostCancellations: s.pushHostCancellations,
      pushHostPayouts: s.pushHostPayouts,
      pushPromotions: s.pushPromotions,
      emailEnabled: s.emailEnabled,
      smsEnabled: s.smsEnabled,
    }
  },

  reset: () => {
    try {
      storage.delete(STORAGE_KEY)
    } catch {
      // SSR or storage unavailable
    }
    set({ ...DEFAULT_NOTIFICATION_PREFS, hasHydrated: false })
  },
}))
